import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  PaperPlaneTilt,
  CaretLeft,
  ArrowSquareIn,
  Camera,
  ArrowSquareOut,
  FilePdf,
} from '@phosphor-icons/react';
import { Link } from 'react-router';
import { useState, useRef, useEffect } from 'react';
import { Api, useApiCallable } from '../../api';
import { serverImageUrl } from '../../util';
import { Model } from '../../models';
import { StackedAvatars } from '../../components/chat';
import { useUserStatusContext } from '../../contexts';
import { useInvalidation, type ReadResult } from 'api-read-hook';
import type { HydraCollection } from '../../types';
import { FullScreenMediaModal } from '../../components';

type ActiveChat = Api.ChatConversation.V_Active;
type ArchivedChat = Api.ChatConversation.V_Archived;
type BroadcastChat = Api.ChatConversation.V_Broadcast;
type ChatMessage = Api.ChatMessage.V_ChatConversationSub;

type ChatUser = ActiveChat['user'];
type ChatTeam =
  | (BroadcastChat['team'] & {
      teamMembers: BroadcastChat['teamMembers'];
    })
  | null;

type Props = {
  messagesApi: ReadResult<HydraCollection<ChatMessage>>;
  chatsApi:
    | ReadResult<HydraCollection<ActiveChat>>
    | ReadResult<HydraCollection<ArchivedChat>>
    | ReadResult<HydraCollection<BroadcastChat>>;
  user: ChatUser;
  team: ChatTeam;
  chatType: 'active' | 'broadcast' | 'archived';
  userId?: string;
  teamId?: string;
  entityType?: 'user' | 'team';
};

export default function ChatDetailLoaded({
  messagesApi,
  chatsApi,
  user,
  team,
  chatType,
  userId,
  teamId,
  entityType,
}: Props) {
  const { myClinicianStatus } = useUserStatusContext();
  const { invalidateExact } = useInvalidation();
  const [messageInput, setMessageInput] = useState('');
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const [pendingMediaIds, setPendingMediaIds] = useState<string[]>([]);
  const [pendingFiles, setPendingFiles] = useState<
    { url: string; type: string; name: string }[]
  >([]);
  const [expandedImageSrc, setExpandedImageSrc] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const callApi = useApiCallable();

  const messagesData = messagesApi?.data;
  const messages = messagesData?.member || [];

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  useEffect(() => {
    scrollToBottom('instant');
  }, []);

  useEffect(() => {
    invalidateExact('/my-clinician-status');
  }, [invalidateExact]);

  useEffect(() => {
    scrollToBottom();
  }, [messagesData]);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    setUploadingFiles(fileArray);

    try {
      const mediaIds = [];
      const fileItems = [];
      for (const file of fileArray) {
        const response = await callApi<Api.MediaUpload.BaseView, object>(
          '/media-uploads',
          {
            method: 'POST',
            uploadFile: file,
            formBody: { type: 'content' },
          },
        );
        if (response) {
          mediaIds.push(response.data['@id']);
          fileItems.push({
            url: URL.createObjectURL(file),
            type: file.type,
            name: file.name,
          });
        }
      }

      setPendingMediaIds(mediaIds);
      setPendingFiles(fileItems);
    } catch (error) {
      console.error('Failed to upload files:', error);
    } finally {
      setUploadingFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSendMessage = async () => {
    const textMessage = messageInput.trim();
    if (!textMessage && pendingMediaIds.length === 0) return;

    try {
      const requestBody: any = {
        message: textMessage || null,
        media: pendingMediaIds,
      };

      if (chatType === 'active' || chatType === 'archived') {
        if (!userId) return;
        requestBody.toUser = `/users/${userId}`;
        requestBody.toTeam = null;
      } else if (chatType === 'broadcast') {
        if (entityType === 'team') {
          if (!teamId) return;
          requestBody.toUser = null;
          requestBody.toTeam = `/organisation-teams/${teamId}`;
        } else {
          if (!userId) return;
          requestBody.toUser = `/users/${userId}`;
          requestBody.toTeam = null;
        }
      }

      await callApi('/chat-messages/as-clinician', {
        method: 'POST',
        jsonBody: requestBody,
      });

      setMessageInput('');
      setPendingMediaIds([]);
      setPendingFiles([]);

      chatsApi.invalidate();
      messagesApi.invalidate();
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const reversedMessages = [...messages].reverse();

  const displayName = user?.name || team?.name || 'Unknown';

  const backLink = `/chat/${chatType}`;

  const isInputDisabled = chatType === 'archived';

  const clinicianDisplayPicture = myClinicianStatus.user.displayPicture;
  const clinicianName = myClinicianStatus.user.name;
  const clinicianInitial = clinicianName.charAt(0).toUpperCase();

  return (
    <>
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            borderBottom: '1px solid #E4E7EC',
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton component={Link} to={backLink} size="small">
              <CaretLeft size={24} color="#667085" />
            </IconButton>
            <StackedAvatars
              displayPictureIds={
                chatType === 'broadcast' && entityType === 'team' && team
                  ? (team.teamMembers || [])
                      .map((member: any) => member.displayPicture?.id)
                      .filter(Boolean)
                  : user?.displayPicture
                    ? [user.displayPicture.id]
                    : []
              }
              fallbackName={displayName}
              size="small"
            />
            <Typography variant="h6" fontWeight={500}>
              {displayName}
              {team?.organisation && ` (${team.organisation.name})`}
            </Typography>
            {user && (
              <Button
                variant="text"
                size="small"
                startIcon={<ArrowSquareIn size={16} />}
                component={Link}
                to={`/users/${userId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open Profile
              </Button>
            )}
            {chatType === 'broadcast' && entityType === 'team' && team && (
              <Button
                variant="text"
                size="small"
                component={Link}
                to={`/users?organisationTeam=${teamId}&activeSubscription=true`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Show all clients
              </Button>
            )}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={2}>
            <Chip
              label={
                chatType === 'active'
                  ? 'Active'
                  : chatType === 'broadcast'
                    ? 'Broadcast'
                    : 'Archived'
              }
              color={
                chatType === 'active'
                  ? 'success'
                  : chatType === 'broadcast'
                    ? 'info'
                    : 'default'
              }
            />
          </Stack>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            px: 3,
            py: 3,
            minHeight: 0,
          }}
        >
          <Stack spacing={3}>
            {reversedMessages.map((message) => (
              <Stack
                key={message.id}
                direction="row"
                spacing={1.5}
                alignItems="flex-start"
                sx={{
                  justifyContent: !message.isMine ? 'flex-start' : 'flex-end',
                }}
              >
                {!message.isMine && (
                  <StackedAvatars
                    displayPictureIds={
                      chatType === 'broadcast' && entityType === 'team' && team
                        ? (team.teamMembers || [])
                            .map((member: any) => member.displayPicture?.id)
                            .filter(Boolean)
                        : user?.displayPicture
                          ? [user.displayPicture.id]
                          : []
                    }
                    fallbackName={displayName}
                    size="small"
                  />
                )}

                <Stack
                  spacing={1}
                  alignItems={!message.isMine ? 'flex-start' : 'flex-end'}
                  sx={{ maxWidth: '452px' }}
                >
                  <Box
                    sx={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      padding: 2,
                      boxShadow:
                        '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
                      border: '1px solid #E5E7EB',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#374151',
                        mb: 1,
                      }}
                    >
                      {!message.isMine ? displayName : 'Me'}
                    </Typography>

                    {message.media && message.media.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        {message.media.map((mediaItem: any, index: number) => (
                          <Box
                            key={mediaItem.id}
                            sx={{
                              borderRadius: '8px',
                              overflow: 'hidden',
                              mb: index < message.media.length - 1 ? 1 : 0,
                            }}
                          >
                            {mediaItem.mimeType === 'application/pdf' ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  p: 2,
                                  backgroundColor: '#F3F4F6',
                                  border: '1px solid #E5E7EB',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    backgroundColor: '#E5E7EB',
                                  },
                                }}
                                onClick={() => {
                                  const url = serverImageUrl({
                                    id: mediaItem.id,
                                  });
                                  window.open(url, '_blank');
                                }}
                              >
                                <FilePdf size={24} color="#EF4444" />
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  {mediaItem.originalFileName}
                                </Typography>
                                <ArrowSquareOut size={20} color="#6B7280" />
                              </Box>
                            ) : (
                              <Box
                                component="img"
                                src={serverImageUrl({
                                  id: mediaItem.id,
                                  w: 280,
                                })}
                                alt="Uploaded image"
                                onClick={() =>
                                  setExpandedImageSrc(
                                    serverImageUrl({ id: mediaItem.id }),
                                  )
                                }
                                sx={{
                                  maxWidth: 280,
                                  display: 'block',
                                  cursor: 'pointer',
                                }}
                              />
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}

                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '14px',
                        lineHeight: 1.5,
                        color: '#374151',
                      }}
                    >
                      {message.message || ''}
                    </Typography>
                  </Box>

                  {message.createdAt && (
                    <Typography
                      variant="caption"
                      sx={{
                        textAlign: !message.isMine ? 'left' : 'right',
                      }}
                    >
                      {Model.Chat.formatTimeAgo(message.createdAt)}
                    </Typography>
                  )}
                </Stack>

                {message.isMine && (
                  <Avatar
                    src={
                      clinicianDisplayPicture
                        ? serverImageUrl({
                            id: clinicianDisplayPicture.id,
                            w: 32,
                          })
                        : undefined
                    }
                    sx={{
                      width: 32,
                      height: 32,
                      backgroundColor: '#E4E7EC',
                      color: '#475467',
                      fontSize: '12px',
                      fontWeight: 500,
                    }}
                  >
                    {clinicianInitial}
                  </Avatar>
                )}
              </Stack>
            ))}
            <div ref={messagesEndRef} />
          </Stack>
        </Box>

        {!isInputDisabled && (
          <Box
            sx={{
              backgroundColor: '#F9FAFB',
              px: 3,
              py: 2,
              flexShrink: 0,
            }}
          >
            {pendingFiles.length > 0 && (
              <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                {pendingFiles.map((fileItem, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: 'relative',
                      width: 80,
                      height: 80,
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1px solid #E5E7EB',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor:
                        fileItem.type === 'application/pdf'
                          ? '#F3F4F6'
                          : 'transparent',
                    }}
                  >
                    {fileItem.type === 'application/pdf' ? (
                      <FilePdf size={32} color="#EF4444" />
                    ) : (
                      <Box
                        component="img"
                        src={fileItem.url}
                        alt="Pending upload"
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={
                  clinicianDisplayPicture
                    ? serverImageUrl({ id: clinicianDisplayPicture.id, w: 32 })
                    : undefined
                }
                sx={{
                  width: 32,
                  height: 32,
                  backgroundColor: '#E4E7EC',
                  color: '#475467',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                {clinicianInitial}
              </Avatar>
              <TextField
                fullWidth
                placeholder="Write a message..."
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                }}
              />
              <Stack direction="row" spacing={0}>
                <IconButton
                  onClick={() => handleSendMessage()}
                  disabled={
                    !messageInput.trim() && pendingMediaIds.length === 0
                  }
                  sx={{
                    color: '#667085',
                    '&:hover': {
                      backgroundColor: '#F3F4F6',
                    },
                    '&:disabled': {
                      color: '#D1D5DB',
                    },
                  }}
                >
                  <PaperPlaneTilt size={24} />
                </IconButton>
                <IconButton
                  onClick={handleImageClick}
                  disabled={uploadingFiles.length > 0}
                  sx={{
                    color: '#667085',
                    '&:hover': {
                      backgroundColor: '#F3F4F6',
                    },
                    '&:disabled': {
                      color: '#D1D5DB',
                    },
                  }}
                >
                  <Camera size={24} />
                </IconButton>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,application/pdf"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
              </Stack>
            </Box>
          </Box>
        )}

        {isInputDisabled && (
          <Box
            sx={{
              backgroundColor: '#F9FAFB',
              px: 3,
              py: 2,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              This is an archived chat. You cannot send new messages.
            </Typography>
          </Box>
        )}
      </Box>
      {expandedImageSrc && (
        <FullScreenMediaModal
          src={expandedImageSrc}
          alt="Chat image"
          onClose={() => setExpandedImageSrc(null)}
        />
      )}
    </>
  );
}
